import React, { useEffect, useState, useCallback } from "react";
import Box from "@mui/material/Box";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import JumboDemoCard from "@jumbo/components/JumboDemoCard/JumboDemoCard";
import {
  uploadToltPayoutCsv,
  getToltPayoutFiles,
} from "backendServices/ApiCalls";
import {
  Button,
  Grid,
  Typography,
  CircularProgress,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import SweetAlert from "app/pages/components/mui/Alerts/SweetAlert";
import Div from "@jumbo/shared/Div/Div";
import { useTheme } from "@mui/material/styles";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DescriptionIcon from "@mui/icons-material/Description";

const ToltPayout = () => {
  const theme = useTheme();
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [alertData, setalertData] = useState({
    show: false,
    message: "",
    variant: "",
  });

  const fetchUploadedFiles = useCallback(() => {
    setLoadingFiles(true);
    getToltPayoutFiles(
      (response) => {
        if (response?.data?.status === "success") {
          const files = response?.data?.data?.map((file, index) => ({
            id: index + 1,
            fileName: file.fileName,
            uploadDate: file.lastModified,
            size: file.size,
          }));
          setUploadedFiles(files || []);
        }
        setLoadingFiles(false);
      },
      (error) => {
        setLoadingFiles(false);
      }
    );
  }, []);

  useEffect(() => {
    fetchUploadedFiles();
  }, [fetchUploadedFiles]);

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (!file.name.endsWith(".csv")) {
        setalertData({
          show: true,
          message: "Please select a CSV file only",
          variant: "error",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (!file.name.endsWith(".csv")) {
        setalertData({
          show: true,
          message: "Please select a CSV file only",
          variant: "error",
        });
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = () => {
    if (!selectedFile) {
      setalertData({
        show: true,
        message: "Please select a CSV file to upload",
        variant: "error",
      });
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", selectedFile);

    uploadToltPayoutCsv(
      formData,
      (response) => {
        setUploading(false);
        if (response?.data?.status === "success") {
          setalertData({
            show: true,
            message: response?.data?.message || "File uploaded successfully",
            variant: "success",
          });
          setSelectedFile(null);
          // Reset file input
          const fileInput = document.getElementById("tolt-csv-upload");
          if (fileInput) fileInput.value = "";
          fetchUploadedFiles();
        } else {
          setalertData({
            show: true,
            message:
              response?.data?.message || "Upload failed. Please try again.",
            variant: "error",
          });
        }
      },
      (error) => {
        setUploading(false);
        setalertData({
          show: true,
          message: "Upload failed. Please try again later.",
          variant: "error",
        });
      }
    );
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const fileColumns = [
    {
      field: "id",
      headerName: "#",
      width: 80,
    },
    {
    field: "fileName",
    headerName: "File Name",
    flex: 1,
    minWidth: 300,
    renderCell: (params) => {
      // If your API already gives full URL
      const fileUrl =
        params.row.file_url ||
        `https://novalya-assets.s3.amazonaws.com/files/csv/tolt-payout/${params.value}`;

      return (
        <Div sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DescriptionIcon
            sx={{ color: theme.palette.primary.main, fontSize: 20 }}
          />

          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              color: "#1976d2",
              textDecoration: "none",
              fontWeight: 500,
            }}
          >
            {params.value}
          </a>
        </Div>
      );
    },
  },
    {
      field: "uploadDate",
      headerName: "Upload Date",
      width: 220,
      valueGetter: (params) => {
        const date = params.value;
        if (!date) return "N/A";
        return new Date(date).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      },
    },
    {
      field: "size",
      headerName: "Size",
      width: 120,
      valueGetter: (params) => formatFileSize(params.value),
    },
  ];

  const gridDesign = {
    "& .MuiDataGrid-toolbarContainer": {
      "& .MuiButton-text": {
        fontSize: "13px !important",
      },
      "& .MuiBadge-badge": {
        backgroundColor: "#074682",
      },
      "& .MuiInput-root": {
        borderRadius: 2,
        paddingLeft: 2,
        overflow: "hidden",
      },
    },
  };

  return (
    <Grid
      container
      fullWidth
      sm={12}
      xs={12}
      p={2}
      alignItems="center"
      justifyContent="center"
    >
      <Grid item sm={12} xs={12}>
        {alertData.show && (
          <SweetAlert alertData={alertData} setalertData={setalertData} />
        )}

        {/* Upload Section */}
        <JumboDemoCard
          title="Upload Tolt Payout CSV"
          wrapperSx={{ backgroundColor: "background.paper", pt: 0 }}
        >
          <Box sx={{ p: 3 }}>
            {/* Drag & Drop Zone */}
            <Paper
              elevation={0}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              sx={{
                border: `2px dashed ${
                  dragActive
                    ? theme.palette.primary.main
                    : selectedFile
                    ? theme.palette.success.main
                    : theme.palette.divider
                }`,
                borderRadius: 3,
                p: 4,
                textAlign: "center",
                cursor: "pointer",
                transition: "all 0.3s ease",
                backgroundColor: dragActive
                  ? theme.palette.mode === "dark"
                    ? "rgba(25, 118, 210, 0.08)"
                    : "rgba(25, 118, 210, 0.04)"
                  : selectedFile
                  ? theme.palette.mode === "dark"
                    ? "rgba(76, 175, 80, 0.08)"
                    : "rgba(76, 175, 80, 0.04)"
                  : "transparent",
                "&:hover": {
                  borderColor: theme.palette.primary.main,
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(25, 118, 210, 0.08)"
                      : "rgba(25, 118, 210, 0.04)",
                },
              }}
              onClick={() =>
                document.getElementById("tolt-csv-upload")?.click()
              }
            >
              <input
                type="file"
                id="tolt-csv-upload"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: "none" }}
              />

              {selectedFile ? (
                <Box>
                  <CheckCircleIcon
                    sx={{
                      fontSize: 48,
                      color: theme.palette.success.main,
                      mb: 1,
                    }}
                  />
                  <Typography variant="h6" gutterBottom>
                    File Selected
                  </Typography>
                  <Chip
                    icon={<InsertDriveFileIcon />}
                    label={`${selectedFile.name} (${formatFileSize(
                      selectedFile.size
                    )})`}
                    color="success"
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                </Box>
              ) : (
                <Box>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: dragActive
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                      mb: 1,
                    }}
                  />
                  <Typography variant="h6" gutterBottom>
                    Drag & Drop CSV File Here
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    or click to browse files
                  </Typography>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{ mt: 1, display: "block" }}
                  >
                    Only .csv files are accepted
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Upload Progress */}
            {uploading && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress />
                <Typography
                  variant="body2"
                  color="textSecondary"
                  sx={{ mt: 1, textAlign: "center" }}
                >
                  Uploading file...
                </Typography>
              </Box>
            )}

            {/* Upload Button */}
            <Div
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  uploading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <CloudUploadIcon />
                  )
                }
                onClick={handleUpload}
                disabled={!selectedFile || uploading}
                sx={{
                  px: 4,
                  py: 1.2,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 600,
                }}
              >
                {uploading ? "Uploading..." : "Upload CSV"}
              </Button>

              {selectedFile && (
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setSelectedFile(null);
                    const fileInput =
                      document.getElementById("tolt-csv-upload");
                    if (fileInput) fileInput.value = "";
                  }}
                  sx={{
                    px: 4,
                    py: 1.2,
                    borderRadius: 2,
                    textTransform: "none",
                  }}
                >
                  Clear
                </Button>
              )}
            </Div>
          </Box>
        </JumboDemoCard>

        {/* Recent Uploads Section */}
        <Box sx={{ mt: 3 }}>
          <JumboDemoCard
            title="Recent Uploaded CSV Files"
            wrapperSx={{ backgroundColor: "background.paper", pt: 0 }}
          >
            <Box sx={{ height: 400, width: 1 }}>
              {loadingFiles ? (
                <Div
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                </Div>
              ) : (
                <DataGrid
                  initialState={{
                    pagination: { paginationModel: { pageSize: 6 } },
                  }}
                  rows={uploadedFiles}
                  getRowId={(row) => row.id}
                  columns={fileColumns}
                  slots={{
                    toolbar: (props) => (
                      <GridToolbar
                        {...props}
                        sx={{
                          backgroundColor:
                            theme.palette.mode === "dark" ? "#424242" : "#fff",
                          "& .MuiIconButton-root": {
                            color:
                              theme.palette.mode === "dark" ? "#fff" : "#000",
                          },
                        }}
                      />
                    ),
                  }}
                  sx={gridDesign}
                  pageSizeOptions={[6, 12, 18, 24, 30]}
                  slotProps={{
                    toolbar: {
                      showQuickFilter: true,
                      quickFilterProps: { debounceMs: 500 },
                    },
                  }}
                />
              )}
            </Box>
          </JumboDemoCard>
        </Box>
      </Grid>
    </Grid>
  );
};

export default ToltPayout;
